import { apiService } from './api';
import { Lab, CreateLabDto, UpdateLabDto, LabListRequest, LabListResponse } from '../types/lab';

export const labsService = {
  // Get all labs (unpaginated — fetches first 500 for dropdowns)
  getAll: async (): Promise<{ success: boolean; data: Lab[]; message?: string }> => {
    const response = await apiService.get<LabListResponse>('/labs', { pageSize: 500 });
    return {
      success: response.success,
      data: response.data?.items ?? [],
      message: response.message,
    };
  },

  // Get labs with pagination
  getLabs: async (params: LabListRequest = {}): Promise<LabListResponse> => {
    const response = await apiService.get<LabListResponse>('/labs', params);
    return response.data;
  },

  // Get lab by ID
  getById: async (id: number): Promise<Lab> => {
    const response = await apiService.get<Lab>(`/labs/${id}`);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch lab');
    }
    return response.data;
  },

  // Create new lab
  create: async (data: CreateLabDto): Promise<Lab> => {
    const response = await apiService.post<Lab>('/labs', data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to create lab');
    }

    return response.data;
  },

  // Update lab
  update: async (id: number, data: UpdateLabDto): Promise<Lab> => {
    const response = await apiService.put<Lab>(`/labs/${id}`, data);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update lab');
    }

    return response.data;
  },

  // Delete lab
  delete: async (id: number): Promise<void> => {
    const response = await apiService.delete<void>(`/labs/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete lab');
    }
  },

  // Search labs
  search: async (searchTerm: string, limit: number = 20): Promise<Lab[]> => {
    const response = await apiService.get<Lab[]>(`/labs/search?searchTerm=${encodeURIComponent(searchTerm)}&limit=${limit}`);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to search labs');
    }

    return response.data;
  },
};
